import * as React from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  Table as ReactTable,
} from "@tanstack/react-table";
import {
  ChevronDown,
  CircleOff,
  CircleCheck,
  CircleX,
  CircleHelp,
  Search,
} from "lucide-react";
import { usePersistentTableState } from "@pushpress/react-hooks/table";

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DataTablePagination,
  DataTableFacetedFilter,
  Input,
} from "@pushpress/pushpress-ui";
import { MatchStateType, MatchState } from "../../../types";

interface SearchInputProps {
  table: ReactTable<any>;
  columnId: string;
}

// Extracted search input component
const SearchInput = ({ table, columnId }: SearchInputProps) => {
  const [searchValue, setSearchValue] = React.useState("");

  React.useEffect(() => {
    if (searchValue) {
      table.getColumn(columnId)?.setFilterValue(searchValue);
    } else {
      table.getColumn(columnId)?.setFilterValue("");
    }
  }, [searchValue, table, columnId]);

  return (
    <div className="tw-relative">
      <Search className="tw-absolute tw-left-3 tw-top-2.5 tw-h-4 tw-w-4 tw-text-gray-500" />
      <Input
        placeholder="Search by name or email"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        className="tw-pl-10 tw-min-w-[240px] tw-bg-white"
      />
    </div>
  );
};

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

const matchStateOptions = [
  {
    label: "Unmatched",
    value: MatchState.UNMATCHED,
    icon: () => <CircleOff className="tw-text-warning" />,
  },
  {
    label: "Confirmed",
    value: MatchState.CONFIRMED,
    icon: () => <CircleCheck className="tw-text-success" />,
  },
  {
    label: "Pending",
    value: MatchState.PENDING,
    icon: () => <CircleHelp className="tw-text-info" />,
  },
  {
    label: "Rejected",
    value: MatchState.REJECTED,
    icon: () => <CircleX className="tw-text-error" />,
  },
];

export function DataTable<
  TData extends {
    matchState?: MatchStateType | null;
    firstName?: string;
    lastName?: string;
    email?: string;
  },
  TValue,
>({ columns, data }: DataTableProps<TData, TValue>) {
  const [state, setters] = usePersistentTableState("payment-matching-table", {
    pagination: { pageIndex: 0, pageSize: 30 },
  });

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting: state.sorting,
      pagination: state.pagination,
      columnFilters: state.columnFilters,
      globalFilter: state.globalFilter,
      columnVisibility: state.columnVisibility,
      rowSelection: state.rowSelection,
      columnOrder: state.columnOrder,
    },
    onSortingChange: setters.setSorting,
    onPaginationChange: setters.setPagination,
    onColumnFiltersChange: setters.setColumnFilters,
    onGlobalFilterChange: setters.setGlobalFilter,
    onColumnVisibilityChange: setters.setColumnVisibility,
    onRowSelectionChange: setters.setRowSelection,
    onColumnOrderChange: setters.setColumnOrder,
    autoResetPageIndex: false,
  });

  return (
    <div className="tw-w-full">
      <div className="tw-flex tw-items-center tw-justify-between tw-py-4 tw-gap-2">
        <div className="tw-flex tw-gap-2 tw-items-center">
          <SearchInput table={table} columnId="name" />
        </div>
        <div className="tw-flex tw-gap-2 tw-items-center">
          <DataTableFacetedFilter
            column={table.getColumn("matchState")}
            title="Status"
            options={matchStateOptions}
          />
        </div>

        <div className="tw-ml-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="tw-ml-auto">
                Columns <ChevronDown className="tw-ml-2 tw-h-4 tw-w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="tw-capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="tw-rounded-t-md tw-border-t tw-border-l tw-border-r tw-bg-white">
        <Table className="tw-border-b">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      className="tw-bg-neutral-100 tw-text-neutral-800"
                      key={header.id}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className="tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="tw-h-24 tw-text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination table={table} />
    </div>
  );
}
